/**
 * Satellite Imagery Service
 * 
 * Implements satellite imagery integration as per PRD FR-003:
 * - Sentinel-2 integration (10m resolution)
 * - Landsat 8 integration (30m resolution)
 * - NDVI calculation and classification
 * - Cloud cover filtering
 * - Time-series analysis
 * 
 * Reference: PRD Section 5.1, Feature 3
 */

/**
 * Satellite data sources configuration
 */
export const SATELLITE_SOURCES = {
  SENTINEL2: {
    name: 'Sentinel-2',
    resolution: '10m',
    provider: 'Copernicus/ESA',
    bands: {
      RED: 'B04',
      NIR: 'B08',
      GREEN: 'B03',
      BLUE: 'B02'
    },
    updateFrequency: '5 days'
  },
  LANDSAT8: {
    name: 'Landsat 8',
    resolution: '30m',
    provider: 'USGS/NASA',
    bands: {
      RED: 'B4',
      NIR: 'B5',
      GREEN: 'B3',
      BLUE: 'B2'
    },
    updateFrequency: '16 days'
  }
};

/**
 * NDVI Classification levels per PRD
 */
export const NDVI_CLASSIFICATIONS = {
  EXCELLENT: {
    label: 'Excellent',
    min: 0.6,
    max: 1.0,
    color: '#15803d',
    description: 'Dense, healthy vegetation'
  },
  GOOD: {
    label: 'Good',
    min: 0.4,
    max: 0.6,
    color: '#22c55e',
    description: 'Moderate vegetation health'
  },
  MODERATE: {
    label: 'Moderate',
    min: 0.2,
    max: 0.4,
    color: '#eab308',
    description: 'Sparse vegetation or early growth'
  },
  POOR: {
    label: 'Poor',
    min: 0.0,
    max: 0.2,
    color: '#f97316',
    description: 'Stressed vegetation or bare soil'
  },
  NO_VEGETATION: {
    label: 'No Vegetation',
    min: -1.0,
    max: 0.0,
    color: '#a3a3a3',
    description: 'Water, built-up areas, or barren land'
  }
};

/**
 * Calculate NDVI (Normalized Difference Vegetation Index)
 * NDVI = (NIR - RED) / (NIR + RED)
 * 
 * @param {number} nir - Near-infrared reflectance value
 * @param {number} red - Red band reflectance value
 * @returns {number} NDVI value (-1 to 1)
 */
export const calculateNDVI = (nir, red) => {
  if (nir + red === 0) return 0;
  const ndvi = (nir - red) / (nir + red);
  return Math.max(-1, Math.min(1, ndvi));
};

/**
 * Calculate NDVI for an array of pixel data
 * 
 * @param {Object[]} pixelData - Array of {nir, red} values
 * @returns {number[]} Array of NDVI values
 */
export const calculateNDVIArray = (pixelData) => {
  return pixelData.map(pixel => calculateNDVI(pixel.nir, pixel.red));
};

/**
 * Classify NDVI value into crop health category
 * 
 * @param {number} ndvi - NDVI value (-1 to 1)
 * @returns {Object} Classification object
 */
export const classifyNDVI = (ndvi) => {
  if (ndvi >= 0.6) return NDVI_CLASSIFICATIONS.EXCELLENT;
  if (ndvi >= 0.4) return NDVI_CLASSIFICATIONS.GOOD;
  if (ndvi >= 0.2) return NDVI_CLASSIFICATIONS.MODERATE;
  if (ndvi >= 0.0) return NDVI_CLASSIFICATIONS.POOR;
  return NDVI_CLASSIFICATIONS.NO_VEGETATION;
};

/**
 * Calculate field-level NDVI statistics
 * 
 * @param {number[]} ndviValues - Array of NDVI values for a field
 * @returns {Object} Field statistics
 */
export const calculateFieldNDVIStats = (ndviValues) => {
  if (!ndviValues || ndviValues.length === 0) {
    return {
      mean: 0,
      median: 0,
      min: 0,
      max: 0,
      stdDev: 0,
      classification: NDVI_CLASSIFICATIONS.NO_VEGETATION,
      healthPercentages: {}
    };
  }

  const sorted = [...ndviValues].sort((a, b) => a - b);
  const n = sorted.length;

  const mean = ndviValues.reduce((a, b) => a + b, 0) / n;
  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];
  const min = sorted[0];
  const max = sorted[n - 1];

  const squaredDiffs = ndviValues.map(v => Math.pow(v - mean, 2));
  const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / n);

  // Calculate health distribution
  const healthCounts = {
    excellent: 0,
    good: 0,
    moderate: 0,
    poor: 0,
    noVegetation: 0
  };

  ndviValues.forEach(ndvi => {
    const classification = classifyNDVI(ndvi);
    if (classification.label === 'Excellent') healthCounts.excellent++;
    else if (classification.label === 'Good') healthCounts.good++;
    else if (classification.label === 'Moderate') healthCounts.moderate++;
    else if (classification.label === 'Poor') healthCounts.poor++;
    else healthCounts.noVegetation++;
  });

  const healthPercentages = {
    excellent: Math.round((healthCounts.excellent / n) * 100),
    good: Math.round((healthCounts.good / n) * 100),
    moderate: Math.round((healthCounts.moderate / n) * 100),
    poor: Math.round((healthCounts.poor / n) * 100),
    noVegetation: Math.round((healthCounts.noVegetation / n) * 100)
  };

  return {
    mean: Math.round(mean * 1000) / 1000,
    median: Math.round(median * 1000) / 1000,
    min: Math.round(min * 1000) / 1000,
    max: Math.round(max * 1000) / 1000,
    stdDev: Math.round(stdDev * 1000) / 1000,
    classification: classifyNDVI(mean),
    healthPercentages,
    pixelCount: n
  };
};

/**
 * Detect significant vegetation changes between two time periods
 * 
 * @param {number[]} previousNDVI - NDVI values from earlier date
 * @param {number[]} currentNDVI - NDVI values from later date
 * @param {number} threshold - Change threshold (default 0.1)
 * @returns {Object} Change detection results
 */
export const detectVegetationChange = (previousNDVI, currentNDVI, threshold = 0.1) => {
  if (!previousNDVI || !currentNDVI || previousNDVI.length !== currentNDVI.length) {
    return {
      error: 'Invalid or mismatched NDVI arrays',
      significantChange: false
    };
  }

  const changes = currentNDVI.map((current, i) => current - previousNDVI[i]);
  const meanChange = changes.reduce((a, b) => a + b, 0) / changes.length;

  let improvement = 0;
  let decline = 0;
  let stable = 0;

  changes.forEach(change => {
    if (change > threshold) improvement++;
    else if (change < -threshold) decline++;
    else stable++;
  });

  const n = changes.length;

  return {
    meanChange: Math.round(meanChange * 1000) / 1000,
    changePercentages: {
      improvement: Math.round((improvement / n) * 100),
      decline: Math.round((decline / n) * 100),
      stable: Math.round((stable / n) * 100)
    },
    significantChange: Math.abs(meanChange) > threshold,
    changeDirection: meanChange > threshold ? 'improving' : meanChange < -threshold ? 'declining' : 'stable',
    alerts: generateChangeAlerts(meanChange, { improvement, decline, stable }, n)
  };
};

/**
 * Generate alerts for significant vegetation changes
 */
const generateChangeAlerts = (meanChange, counts, total) => {
  const alerts = [];

  if (counts.decline / total > 0.3) {
    alerts.push({
      severity: 'warning',
      message: `${Math.round((counts.decline / total) * 100)}% of area showing vegetation decline`,
      action: 'Investigate potential stress factors (drought, pests, disease)'
    });
  }

  if (meanChange < -0.2) {
    alerts.push({
      severity: 'critical',
      message: 'Significant vegetation health decline detected',
      action: 'Immediate field inspection recommended'
    });
  }

  if (counts.improvement / total > 0.5 && meanChange > 0.1) {
    alerts.push({
      severity: 'info',
      message: 'Positive vegetation recovery trend observed',
      action: 'Continue current management practices'
    });
  }

  return alerts;
};

/**
 * Check if imagery meets cloud cover threshold
 * 
 * @param {number} cloudCover - Cloud cover percentage (0-100)
 * @param {number} threshold - Maximum acceptable cloud cover (default 20%)
 * @returns {boolean} True if image is acceptable
 */
export const isImageryAcceptable = (cloudCover, threshold = 20) => {
  return cloudCover <= threshold;
};

/**
 * Calculate time-series NDVI trend
 * 
 * @param {Object[]} timeSeries - Array of {date, ndvi} objects
 * @returns {Object} Trend analysis results
 */
export const analyzeNDVITimeSeries = (timeSeries) => {
  if (!timeSeries || timeSeries.length < 2) {
    return {
      trend: 'insufficient_data',
      slope: 0,
      seasonality: null
    };
  }

  // Sort by date
  const sorted = [...timeSeries].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Calculate linear trend (simple linear regression)
  const n = sorted.length;
  const xValues = sorted.map((_, i) => i);
  const yValues = sorted.map(d => d.ndvi);

  const sumX = xValues.reduce((a, b) => a + b, 0);
  const sumY = yValues.reduce((a, b) => a + b, 0);
  const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
  const sumX2 = xValues.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Determine trend direction
  let trend;
  if (slope > 0.01) trend = 'improving';
  else if (slope < -0.01) trend = 'declining';
  else trend = 'stable';

  // Calculate seasonal averages if enough data
  let seasonality = null;
  if (n >= 12) {
    const monthly = {};
    sorted.forEach(d => {
      const month = new Date(d.date).getMonth();
      if (!monthly[month]) monthly[month] = [];
      monthly[month].push(d.ndvi);
    });

    seasonality = Object.entries(monthly).map(([month, values]) => ({
      month: parseInt(month),
      avgNDVI: Math.round(values.reduce((a, b) => a + b, 0) / values.length * 1000) / 1000
    }));
  }

  return {
    trend,
    slope: Math.round(slope * 10000) / 10000,
    intercept: Math.round(intercept * 1000) / 1000,
    dataPoints: n,
    firstDate: sorted[0].date,
    lastDate: sorted[n - 1].date,
    seasonality,
    forecast: {
      nextPeriod: Math.round((intercept + slope * n) * 1000) / 1000,
      confidence: n > 10 ? 'high' : n > 5 ? 'medium' : 'low'
    }
  };
};

/**
 * Generate NDVI color for visualization
 * 
 * @param {number} ndvi - NDVI value
 * @returns {string} Hex color code
 */
export const getNDVIColor = (ndvi) => {
  const classification = classifyNDVI(ndvi);
  return classification.color;
};

/**
 * Generate color gradient for NDVI visualization
 * Returns array of [value, color] pairs for MapLibre/Mapbox
 */
export const getNDVIColorGradient = () => {
  return [
    [-1.0, NDVI_CLASSIFICATIONS.NO_VEGETATION.color],
    [0.0, NDVI_CLASSIFICATIONS.POOR.color],
    [0.2, NDVI_CLASSIFICATIONS.MODERATE.color],
    [0.4, NDVI_CLASSIFICATIONS.GOOD.color],
    [0.6, NDVI_CLASSIFICATIONS.EXCELLENT.color],
    [1.0, '#166534'] // Dark green for very high NDVI
  ];
};

/**
 * Process satellite imagery request (mock for now, would connect to real API)
 * 
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} Processed imagery data
 */
export const fetchSatelliteImagery = async (params) => {
  const {
    bounds, // [west, south, east, north]
    startDate,
    endDate,
    source = 'SENTINEL2',
    maxCloudCover = 20
  } = params;

  // This would connect to Sentinel Hub or similar API
  // For now, return mock data structure
  console.log('Fetching satellite imagery:', { bounds, startDate, endDate, source, maxCloudCover });

  // Mock response structure
  return {
    source: SATELLITE_SOURCES[source],
    acquisitionDate: new Date().toISOString(),
    cloudCover: 12,
    bounds,
    resolution: SATELLITE_SOURCES[source].resolution,
    ndviData: {
      mean: 0.45,
      min: -0.1,
      max: 0.82,
      classification: 'Good'
    },
    status: 'success',
    message: 'Mock imagery data - integrate with Sentinel Hub API for production'
  };
};

/**
 * Generate field report from satellite data
 * 
 * @param {Object} fieldData - Field information
 * @param {Object} satelliteData - Satellite analysis results
 * @returns {Object} Formatted report
 */
export const generateSatelliteReport = (fieldData, satelliteData) => {
  return {
    reportId: `SAT-${Date.now()}`,
    generatedAt: new Date().toISOString(),
    field: {
      id: fieldData.id,
      name: fieldData.name,
      area: fieldData.area,
      location: fieldData.location
    },
    imagery: {
      source: satelliteData.source?.name || 'Unknown',
      acquisitionDate: satelliteData.acquisitionDate,
      cloudCover: satelliteData.cloudCover,
      resolution: satelliteData.resolution
    },
    vegetation: {
      ndviMean: satelliteData.ndviData?.mean,
      classification: satelliteData.ndviData?.classification,
      healthStatus: classifyNDVI(satelliteData.ndviData?.mean || 0)
    },
    recommendations: generateSatelliteRecommendations(satelliteData),
    exportFormats: ['PDF', 'GeoTIFF', 'CSV']
  };
};

/**
 * Generate recommendations based on satellite analysis
 */
const generateSatelliteRecommendations = (satelliteData) => {
  const recommendations = [];
  const ndvi = satelliteData.ndviData?.mean || 0;

  if (ndvi < 0.2) {
    recommendations.push({
      priority: 'high',
      category: 'Vegetation Health',
      action: 'Field inspection required - low vegetation indices detected',
      details: 'Consider soil testing and irrigation assessment'
    });
  } else if (ndvi < 0.4) {
    recommendations.push({
      priority: 'medium',
      category: 'Vegetation Health',
      action: 'Monitor closely - vegetation showing moderate stress',
      details: 'Increase monitoring frequency to weekly'
    });
  } else {
    recommendations.push({
      priority: 'low',
      category: 'Vegetation Health',
      action: 'Continue current management - healthy vegetation observed',
      details: 'Maintain regular monitoring schedule'
    });
  }

  return recommendations;
};

const satelliteService = {
  calculateNDVI,
  calculateNDVIArray,
  classifyNDVI,
  calculateFieldNDVIStats,
  detectVegetationChange,
  analyzeNDVITimeSeries,
  getNDVIColor,
  getNDVIColorGradient,
  isImageryAcceptable,
  fetchSatelliteImagery,
  generateSatelliteReport,
  SATELLITE_SOURCES,
  NDVI_CLASSIFICATIONS
};

export default satelliteService;
